import { useEffect, useState, useCallback } from "react";
import { http, API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { MessageSquare, X, User as UserIcon } from "lucide-react";

/**
 * Global background listener that subscribes to the messages SSE
 * stream and pops a large centred banner whenever another operator
 * sends a message. The banner sits above every other UI so the
 * recipient can't miss it — think «Πήγαινε στο Τραπέζι 5» while the
 * waiter is halfway through a payment.
 *
 * Mounted once by Layout.jsx while a user is signed in.
 */
export default function MessagesListener() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [flash, setFlash] = useState(false);
  const msg = queue[0] || null;

  const pushMsg = useCallback((m) => {
    if (!m?.id) return;
    setQueue((q) => (q.some((x) => x.id === m.id) ? q : [...q, m]));
    setFlash(true);
  }, []);

  // Ιούν 2026: μηνύματα που στάλθηκαν όσο το τερματικό ήταν εκτός σύνδεσης
  // εμφανίζονται με το που συνδεθεί/φορτώσει — δεν χάνονται πια.
  const drainUnread = useCallback(async () => {
    try {
      const r = await http.get("/messages/inbox?limit=20");
      const cutoff = Date.now() - 7 * 24 * 3600 * 1000; // μόνο τελευταίες 7 ημέρες
      const unread = (r.data?.messages || [])
        .filter((m) => !m.read && (!m.created_at || new Date(m.created_at).getTime() >= cutoff))
        .reverse();
      unread.forEach((m) => pushMsg({ kind: "message", ...m }));
    } catch { /* offline tolerant */ }
  }, [pushMsg]);

  const acknowledge = useCallback(async (m) => {
    setQueue((q) => q.filter((x) => x.id !== m.id));
    setFlash(false);
    try {
      await http.post("/messages/ack", { message_id: m.id });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;
    const token = (() => {
      try { return localStorage.getItem("auth_token") || ""; } catch { return ""; }
    })();
    // EventSource can't set headers, so pass the JWT as a query param
    // — the backend already accepts it that way for the tables stream.
    const url = `${API}/messages/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url, { withCredentials: false });
    // Αδιάβαστα από όσο ήμασταν αποσυνδεδεμένοι/εκτός.
    drainUnread();
    let opens = 0;
    es.onopen = () => {
      opens += 1;
      if (opens > 1) drainUnread(); // reconnect → μάζεψε όσα χάθηκαν στο κενό
    };

    es.onmessage = (evt) => {
      let payload;
      try { payload = JSON.parse(evt.data); } catch { return; }
      if (payload?.kind !== "message") return;
      pushMsg(payload);
      // Play a short beep — best-effort, browsers may block without
      // a prior user interaction (which every logged-in user has had).
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = 880;
        o.type = "sine";
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        o.connect(g); g.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.35);
      } catch { /* audio blocked — visual banner is enough */ }
    };
    es.onerror = () => { /* EventSource auto-reconnects; nothing to do */ };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!msg) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 pointer-events-auto"
      data-testid="incoming-message-banner"
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`relative max-w-2xl w-full rounded-2xl bg-white shadow-2xl border-4 ${
          flash ? "border-blue-500 animate-pulse" : "border-blue-400"
        }`}
        onAnimationEnd={() => setFlash(false)}
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b-2 border-slate-200 bg-blue-50 rounded-t-xl">
          <div className="w-12 h-12 rounded-full bg-blue-600 text-white grid place-items-center shrink-0">
            <MessageSquare size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-blue-700 font-bold">
              Εισερχόμενο μήνυμα
            </div>
            <div className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
              <UserIcon size={12} className="text-slate-500" />
              {msg.from_user_name || "—"}
              <span className="text-[10px] text-slate-500 font-normal uppercase tracking-wider ml-1">
                {msg.from_user_role || "user"}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => acknowledge(msg)}
            data-testid="incoming-message-close"
            className="p-2 rounded-md text-slate-500 hover:bg-slate-200"
            aria-label="Κλείσιμο"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-8">
          <div
            className="text-2xl font-bold text-slate-900 leading-relaxed text-center whitespace-pre-wrap break-words"
            data-testid="incoming-message-text"
          >
            {msg.text}
          </div>
          {msg.template_label && msg.template_label !== "Ad-hoc" && (
            <div className="mt-3 text-center">
              <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">
                Έτοιμο σχόλιο
              </span>
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t-2 border-slate-100 bg-slate-50 rounded-b-xl flex justify-center">
          <button
            type="button"
            onClick={() => acknowledge(msg)}
            data-testid="incoming-message-ack"
            className="px-8 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-base shadow-md"
          >
            Το είδα ✓
          </button>
        </div>
      </div>
    </div>
  );
}
